import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Award,
  Calendar,
  Clock,
  Crown,
  Flag,
  Heart,
  MapPin,
  Shield,
  ShieldCheck,
  Star,
  UserCheck,
  UserCog,
  UserPlus,
  Users,
  UserX,
} from "lucide-react";
import Avatar from "../../components/ui/Avatar";
import {
  fetchPublicProfileBySlug,
  fetchPublicProfileByUserId,
  fetchProfileComments,
  createProfileComment,
  deleteProfileComment,
} from "../../api/profileService";
import { toggleProfileLike } from "../../api/likeService";
import { sendFriendRequest, respondFriendRequest } from "../../api/premiumSocialService";
import { useAuth } from "../../context/AuthContext";
import { API_BASE_URL } from "../../utils/constants";

const MEDIA_BASE_URL =
  process.env.REACT_APP_MEDIA_BASE_URL || process.env.VITE_API_URL || API_BASE_URL;

function resolveThumb(imagePath) {
  if (!imagePath) return null;
  if (imagePath.startsWith("http")) return imagePath;
  return `${MEDIA_BASE_URL}${imagePath}`;
}

function RoundSquare({ round }) {
  const [hovered, setHovered] = useState(false);
  const thumb = resolveThumb(round.image_path);
  let cls = "flex h-7 w-7 items-center justify-center rounded-md text-[10px] transition select-none cursor-default ";
  let inner;
  const rank = round.answer_rank > 0 ? round.answer_rank : null;
  if (round.is_correct) {
    if (round.is_fast) {
      cls += "bg-amber-100 border border-amber-300";
      inner = <span className="text-[9px] font-black text-amber-600">⚡{rank}</span>;
    } else {
      cls += "bg-emerald-100 border border-emerald-300";
      inner = <span className="text-[11px] font-bold text-emerald-600">✓{rank && <span className="text-[8px] ml-0.5">{rank}</span>}</span>;
    }
  } else {
    cls += "bg-rose-100 border border-rose-200";
    inner = <span className="text-[11px] font-bold text-rose-400">✗</span>;
  }
  const hasPopup = thumb || round.answer_text;
  return (
    <div
      className="relative"
      onMouseEnter={() => hasPopup && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={cls}>{inner}</div>
      {hovered && hasPopup && (
        <div className="absolute bottom-full left-1/2 z-30 mb-2 -translate-x-1/2 rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl" style={{ minWidth: 110, maxWidth: 150 }}>
          {thumb && <img src={thumb} alt="" className="h-16 w-full rounded-lg object-cover" />}
          {round.answer_text && (
            <div className={`mt-1 truncate text-center text-[11px] font-semibold ${round.is_correct ? "text-emerald-600" : "text-rose-500"}`}>
              {round.answer_text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatLastLogin(value) {
  if (!value) return null;
  return new Date(value).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function BadgeIcon({ badge }) {
  if (badge.icon_image) {
    return (
      <img src={badge.icon_image} alt="" className="h-9 w-9 rounded-xl object-cover" />
    );
  }
  return <span className="text-lg">{badge.icon || "*"}</span>;
}

function StatusBadges({ profile }) {
  const badges = [];
  if (profile?.is_moderator) {
    badges.push(
      <span
        key="moderator"
        className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700"
        title="Moderateur"
      >
        <ShieldCheck className="h-3 w-3" />
        Modo
      </span>
    );
  }
  if (profile?.is_admin) {
    badges.push(
      <span
        key="admin"
        className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700"
        title="Administrateur"
      >
        <UserCog className="h-3 w-3" />
        Admin
      </span>
    );
  }
  return badges.length ? (
    <span className="inline-flex flex-wrap items-center gap-1">{badges}</span>
  ) : null;
}

function CategoryCard({ cat }) {
  const best = cat.best_game;
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition hover:border-slate-200 hover:shadow">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-semibold text-pixy-ink">
            {cat.category_name || cat.category}
          </div>
          {cat.category_rank && (
            <div className="mt-0.5 text-[10px] font-semibold text-sky-600">
              #{cat.category_rank} au classement
            </div>
          )}
        </div>
        <span className="shrink-0 text-sm font-bold text-pixy-ink">
          {(cat.score || 0).toLocaleString("fr-FR")} pts
        </span>
      </div>

      {best ? (
        <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 p-3">
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="font-semibold text-pixy-ink">
              Meilleure partie : {best.score.toLocaleString("fr-FR")} pts
            </span>
            <div className="flex items-center gap-1.5 text-slate-400 shrink-0">
              {best.total_players > 0 ? (
                <span className="font-semibold text-sky-600">
                  {best.rank > 0 ? `#${best.rank}` : "?"}/{best.total_players}
                </span>
              ) : null}
              <span>{best.played_at}</span>
            </div>
          </div>
          {best.rounds?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {best.rounds.map((r, i) => (
                <RoundSquare key={i} round={r} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-2 text-xs text-slate-400">Pas d'historique</div>
      )}
    </div>
  );
}

const CONDITION_LABEL = {
  games_played: "parties jouées",
  total_games: "parties jouées",
  category_games_played: "parties dans la catégorie",
  correct_answers: "bonnes réponses",
  category_correct_answers: "bonnes réponses",
  wins: "victoires",
  first_places: "premières places",
  podiums: "podiums",
  total_score: "points",
  best_score: "points (meilleur score)",
};

function BadgeCard({ badge }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white">
        <BadgeIcon badge={badge} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-pixy-ink">{badge.name}</div>
        {badge.level && (
          <div className="mt-0.5 text-xs text-slate-500">Niveau {badge.level}</div>
        )}
        {badge.description && (
          <div className="mt-0.5 text-xs text-slate-400">{badge.description}</div>
        )}
      </div>
    </div>
  );
}

export default function PublicProfile() {
  const { publicSlug, userId } = useParams();
  const { user: currentUser, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [likeError, setLikeError] = useState("");
  const [isLiking, setIsLiking] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [commentError, setCommentError] = useState("");
  const [friendshipStatus, setFriendshipStatus] = useState(null);
  const [friendshipDirection, setFriendshipDirection] = useState(null);
  const [friendshipId, setFriendshipId] = useState(null);
  const [friendError, setFriendError] = useState("");
  const [isFriendLoading, setIsFriendLoading] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const loadProfile = async () => {
      setIsLoading(true);
      setError("");
      try {
        const data = userId
          ? await fetchPublicProfileByUserId(userId, controller.signal)
          : await fetchPublicProfileBySlug(publicSlug, controller.signal);
        setProfile(data);
        setFriendshipStatus(data.friendship_status || null);
        setFriendshipDirection(data.friendship_direction || null);
        setFriendshipId(data.friendship_id || null);
      } catch (loadError) {
        setError(
          loadError instanceof Error ? loadError.message : "Profil public introuvable."
        );
      } finally {
        setIsLoading(false);
      }
    };
    loadProfile();
    return () => controller.abort();
  }, [publicSlug, userId]);

  useEffect(() => {
    if (!profile?.id) return;
    const controller = new AbortController();
    fetchProfileComments(profile.id, controller.signal)
      .then((data) => setComments(data.results || []))
      .catch(() => {});
    return () => controller.abort();
  }, [profile?.id]);

  const isOwnProfile = isAuthenticated && profile?.id === currentUser?.id;

  const handleSendFriendRequest = async () => {
    if (!profile?.id || isFriendLoading) return;
    setIsFriendLoading(true);
    setFriendError("");
    try {
      const data = await sendFriendRequest(profile.id);
      const fs = data.friendship;
      setFriendshipStatus(fs.status);
      setFriendshipDirection(fs.direction);
      setFriendshipId(fs.id);
    } catch (err) {
      setFriendError(err?.response?.data?.error || "Impossible d'envoyer la demande.");
    } finally {
      setIsFriendLoading(false);
    }
  };

  const handleFriendRespond = async (action) => {
    if (!friendshipId || isFriendLoading) return;
    setIsFriendLoading(true);
    setFriendError("");
    try {
      const data = await respondFriendRequest(friendshipId, action);
      if (data.deleted) {
        setFriendshipStatus(null);
        setFriendshipDirection(null);
        setFriendshipId(null);
      } else {
        const fs = data.friendship;
        setFriendshipStatus(fs.status);
        setFriendshipDirection(fs.direction);
        setFriendshipId(fs.id);
      }
    } catch (err) {
      setFriendError(err?.response?.data?.error || "Impossible de traiter la demande.");
    } finally {
      setIsFriendLoading(false);
    }
  };

  const submitLike = async () => {
    if (!profile?.id) return;
    setIsLiking(true);
    setLikeError("");
    try {
      const response = await toggleProfileLike(
        profile.id,
        profile.liked_by_me ? "unlike" : "like"
      );
      setProfile((current) => ({
        ...current,
        likes_count: response.profile.likes_count,
        liked_by_me: response.profile.liked_by_me,
      }));
    } catch (submitError) {
      setLikeError(
        submitError?.response?.data?.error ||
          submitError?.response?.data?.detail ||
          "Connexion requise pour liker ce profil."
      );
    } finally {
      setIsLiking(false);
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    const text = commentText.trim();
    if (!text) return;
    setCommentError("");
    try {
      const data = await createProfileComment(profile.id, { text });
      setComments((prev) => [...prev, data.comment]);
      setCommentText("");
    } catch (err) {
      setCommentError(err?.response?.data?.error || "Connexion requise.");
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await deleteProfileComment(profile.id, commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (err) {
      setCommentError(err?.response?.data?.error || "Impossible de supprimer.");
    }
  };

  const age = useMemo(() => {
    if (!profile?.birth_year) return null;
    const a = new Date().getFullYear() - profile.birth_year;
    return a > 0 && a < 120 ? a : null;
  }, [profile?.birth_year]);

  if (isLoading) {
    return (
      <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,rgba(95,181,111,0.18),transparent_28%),radial-gradient(circle_at_top_right,rgba(47,157,226,0.16),transparent_26%),linear-gradient(180deg,#fffdf5_0%,#effcf6_48%,#e9f7ff_100%)] min-h-screen">
        <div className="mx-auto max-w-[1480px] px-4 py-8 sm:px-6 lg:px-8">
          <div className="h-40 animate-pulse rounded-[36px] bg-white/60" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,rgba(95,181,111,0.18),transparent_28%),linear-gradient(180deg,#fffdf5_0%,#effcf6_48%,#e9f7ff_100%)] min-h-screen">
        <div className="mx-auto max-w-[1480px] px-4 py-8 sm:px-6 lg:px-8">
          <div className="rounded-[32px] border border-rose-200 bg-rose-50 p-8 text-sm text-rose-700 shadow-sm">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const categoryScores = profile.category_scores || [];

  return (
    <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,rgba(95,181,111,0.18),transparent_28%),radial-gradient(circle_at_top_right,rgba(47,157,226,0.16),transparent_26%),linear-gradient(180deg,#fffdf5_0%,#effcf6_48%,#e9f7ff_100%)]">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-[-10%] top-12 h-64 w-64 rounded-full bg-emerald-200/30 blur-3xl" />
        <div className="absolute right-[-8%] top-24 h-72 w-72 rounded-full bg-sky-200/30 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-[1480px] px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <header
          className="mb-6 overflow-hidden rounded-[36px] border border-white/70 bg-white/85 bg-cover bg-center px-5 py-6 shadow-[0_24px_80px_rgba(47,157,226,0.16)] sm:px-7"
          style={{
            backgroundImage: profile.profile_background
              ? `linear-gradient(90deg,rgba(255,253,245,0.92),rgba(233,247,255,0.72)),url(${profile.profile_background})`
              : "linear-gradient(165deg,#fffdf5 0%,#effcf6 55%,#e9f7ff 100%)",
          }}
        >
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            {/* Avatar + pseudo */}
            <div className="flex items-center gap-4">
              <div className="relative shrink-0">
                {profile.avatar ? (
                  <img
                    src={profile.avatar}
                    alt={profile.username}
                    className="h-16 w-16 rounded-2xl border-2 border-white object-cover shadow-md sm:h-20 sm:w-20"
                  />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl border-2 border-white bg-gradient-to-br from-pixy-green/30 to-pixy-blue/30 text-2xl shadow-md sm:h-20 sm:w-20">
                    {profile.username?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
                {profile.is_premium && (
                  <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 shadow">
                    <Crown className="h-3 w-3 text-white" />
                  </span>
                )}
                {profile.is_online && (
                  <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-400 sm:h-4 sm:w-4" />
                )}
              </div>

              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-bold tracking-tight text-pixy-ink sm:text-3xl">
                    {profile.username}
                  </h1>
                  {profile.is_admin && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">
                      <Star className="h-3 w-3" />
                      Admin
                    </span>
                  )}
                  {profile.is_moderator && !profile.is_admin && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700">
                      <Shield className="h-3 w-3" />
                      Modo
                    </span>
                  )}
                  {profile.is_premium && !profile.is_admin && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                      <Crown className="h-3 w-3" />
                      Premium
                    </span>
                  )}
                </div>

                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                  {age !== null && (
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5 text-pixy-blue" />
                      {age} ans
                    </span>
                  )}
                  {profile.city && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5 text-pixy-green" />
                      {profile.city}
                    </span>
                  )}
                </div>
                {profile.date_joined && (
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5 text-pixy-blue" />
                      Inscrit le{" "}
                      {new Intl.DateTimeFormat("fr-FR", {
                        month: "2-digit",
                        year: "numeric",
                      }).format(new Date(profile.date_joined))}
                    </span>
                  </div>
                )}

                {profile.bio && (
                  <p className="mt-2 max-w-xl text-xs leading-relaxed text-slate-600">
                    {profile.bio}
                  </p>
                )}
              </div>
            </div>

            {/* Info chips */}
            <div className="flex items-end gap-4">
              {profile.featured_badge && (() => {
                const badge = profile.featured_badge;
                const imgUrl = badge.icon_image || (badge.icon && badge.icon.startsWith("http") ? badge.icon : null);
                const emoji = !imgUrl && badge.icon ? badge.icon : null;
                return (
                  <div className="flex flex-col items-center gap-1">
                    {imgUrl ? (
                      <img src={imgUrl} alt={badge.name} className="h-16 w-16 object-contain drop-shadow-sm" />
                    ) : emoji ? (
                      <span className="text-5xl leading-none">{emoji}</span>
                    ) : (
                      <Award className="h-16 w-16 text-amber-400" />
                    )}
                    <span className="text-[10px] font-semibold text-amber-700 leading-none">{badge.name}</span>
                  </div>
                );
              })()}
              {profile.team && (
                <Link
                  to={`/equipe/t/${profile.team.id}`}
                  className="flex flex-col items-center gap-1 transition hover:opacity-80"
                >
                  {profile.team.logo ? (
                    <img src={profile.team.logo} alt={profile.team.name} className="h-8 w-8 rounded-xl object-cover border border-white shadow-sm" />
                  ) : (
                    <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-sky-100 border border-white shadow-sm">
                      <Users className="h-4 w-4 text-sky-500" />
                    </div>
                  )}
                  <span className="text-[10px] font-bold text-sky-700">[{profile.team.tag}]</span>
                </Link>
              )}
              <div className="flex flex-col items-center gap-0.5">
                <Heart className="h-5 w-5 text-rose-400" fill="currentColor" />
                <span className="text-xs font-medium text-slate-600">{profile.likes_count}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Main grid */}
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_300px]">
          {/* Left: stats + categories + badges + comments */}
          <div className="space-y-6">
            {/* Stats globales */}
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-2xl border border-sky-100 bg-white/80 px-5 py-4 shadow-sm">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pixy-blue">
                  Classement Général
                </div>
                <div className="mt-1.5 text-3xl font-bold text-pixy-ink">
                  {profile.global_rank != null ? `#${profile.global_rank}` : "—"}
                </div>
              </div>
              <div className="rounded-2xl border border-amber-100 bg-white/80 px-5 py-4 shadow-sm">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-600">
                  Points
                </div>
                <div className="mt-1.5 text-3xl font-bold text-pixy-ink">
                  {(profile.total_points || 0).toLocaleString("fr-FR")}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-white/80 px-5 py-4 shadow-sm">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Parties jouées
                </div>
                <div className="mt-1.5 text-3xl font-bold text-pixy-ink">
                  {profile.games_played ?? 0}
                </div>
              </div>
            </div>

            {/* Catégories jouées */}
            <div>
              <div className="mb-3 text-sm font-semibold text-pixy-ink">
                Catégories jouées
              </div>
              {categoryScores.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white/60 px-4 py-8 text-sm text-slate-500">
                  Aucune partie comptabilisée pour le moment.
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  {categoryScores.map((cat) => (
                    <CategoryCard key={cat.category} cat={cat} />
                  ))}
                </div>
              )}
            </div>

            {/* Badges */}
            <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="text-base font-bold text-pixy-ink">
                Badges ({profile.badges?.length || 0})
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {profile.badges?.length ? (
                  profile.badges.map((badge) => (
                    <BadgeCard key={badge.id} badge={badge} />
                  ))
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500 sm:col-span-2">
                    Aucun badge public pour le moment.
                  </div>
                )}
              </div>
            </div>

            {/* Commentaires */}
            <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-base font-bold text-pixy-ink">
                Commentaires ({comments.length})
              </h2>
              <div className="mt-4 space-y-4">
                {comments.map((comment) => (
                  <div key={comment.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="text-sm font-bold text-slate-900">{comment.author}</span>
                        <span className="ml-2 text-xs text-slate-400">
                          {new Date(comment.created_at).toLocaleDateString("fr-FR")}
                        </span>
                      </div>
                      {isAuthenticated && (currentUser?.id === comment.author_id || isOwnProfile || currentUser?.is_staff) && (
                        <button
                          type="button"
                          onClick={() => handleDelete(comment.id)}
                          className="rounded-lg px-2 py-1 text-xs text-rose-600 hover:bg-rose-50"
                        >
                          Supprimer
                        </button>
                      )}
                    </div>
                    <p className="mt-2 text-sm text-slate-700">{comment.text}</p>
                  </div>
                ))}

                {comments.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                    Aucun commentaire pour le moment.
                  </div>
                )}
              </div>

              {isAuthenticated && (
                <form onSubmit={submitComment} className="mt-4 flex gap-2">
                  <input
                    type="text"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Laisser un commentaire..."
                    className="flex-1 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-sky-400"
                  />
                  <button type="submit" className="rounded-2xl bg-pixy-ink px-5 py-2.5 text-sm font-bold text-white">
                    Publier
                  </button>
                </form>
              )}
              {commentError && (
                <div className="mt-2 text-sm font-semibold text-rose-700">{commentError}</div>
              )}
            </div>
          </div>

          {/* Right panel */}
          <div className="space-y-4">
            {/* Like */}
            <div className="rounded-[28px] border border-white/70 bg-white/85 p-5 shadow-sm">
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pink-500">
                Likes profil
              </div>
              <div className="mt-2 text-3xl font-bold text-pixy-ink">
                {profile.likes_count || 0}
              </div>
              {likeError && (
                <div className="mt-1 text-xs font-semibold text-rose-700">{likeError}</div>
              )}
              <button
                type="button"
                disabled={isLiking}
                onClick={submitLike}
                className={`mt-3 w-full rounded-2xl px-4 py-2.5 text-sm font-bold transition disabled:opacity-60 ${
                  profile.liked_by_me
                    ? "border border-pink-200 bg-white text-pink-700"
                    : "bg-pink-500 text-white hover:bg-pink-600"
                }`}
              >
                {profile.liked_by_me ? "Retirer mon like" : "Liker ce profil"}
              </button>
            </div>

            {/* Amis du joueur */}
            <div className="rounded-[28px] border border-white/70 bg-white/85 p-5 shadow-sm">
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pixy-blue">
                Amis ({profile.friends_count || 0})
              </div>
              {(profile.friends || []).length === 0 ? (
                <p className="mt-2 text-xs text-slate-500">
                  Aucun ami pour le moment.
                </p>
              ) : (
                <div className="mt-3 flex flex-wrap gap-2">
                  {(profile.friends || []).map((friend) => (
                    <Link
                      key={friend.id}
                      to={`/joueur/u/${friend.id}`}
                      title={friend.username}
                      className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-pixy-green/30 to-pixy-blue/30 text-xs font-bold text-pixy-ink ring-2 ring-white transition hover:ring-pixy-blue/40"
                    >
                      {friend.avatar ? (
                        <img
                          src={friend.avatar}
                          alt={friend.username}
                          className="h-9 w-9 rounded-full object-cover"
                        />
                      ) : (
                        (friend.username?.[0] || "?").toUpperCase()
                      )}
                    </Link>
                  ))}
                  {(profile.friends_count || 0) > (profile.friends || []).length && (
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">
                      +{profile.friends_count - profile.friends.length}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Friendship */}
            {isAuthenticated && !isOwnProfile && (
              <div className="rounded-[28px] border border-white/70 bg-white/85 p-5 shadow-sm">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pixy-blue">
                  Amitié
                </div>
                {friendError && (
                  <div className="mt-2 text-xs font-semibold text-rose-700">{friendError}</div>
                )}
                <div className="mt-3">
                  {friendshipStatus === null && (
                    <button
                      type="button"
                      disabled={isFriendLoading}
                      onClick={handleSendFriendRequest}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-pixy-blue px-4 py-2.5 text-sm font-bold text-white transition hover:opacity-90 disabled:opacity-60"
                    >
                      <UserPlus className="h-4 w-4" />
                      Ajouter en ami
                    </button>
                  )}
                  {friendshipStatus === "pending" && friendshipDirection === "sent" && (
                    <div className="space-y-2">
                      <span className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-600">
                        <Clock className="h-4 w-4" />
                        Demande envoyée
                      </span>
                      <button
                        type="button"
                        disabled={isFriendLoading}
                        onClick={() => handleFriendRespond("cancel")}
                        className="w-full rounded-2xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-100 disabled:opacity-60"
                      >
                        Annuler la demande
                      </button>
                    </div>
                  )}
                  {friendshipStatus === "pending" && friendshipDirection === "received" && (
                    <div className="space-y-2">
                      <button
                        type="button"
                        disabled={isFriendLoading}
                        onClick={() => handleFriendRespond("accept")}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:opacity-60"
                      >
                        <UserCheck className="h-4 w-4" />
                        Accepter
                      </button>
                      <button
                        type="button"
                        disabled={isFriendLoading}
                        onClick={() => handleFriendRespond("decline")}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                      >
                        <UserX className="h-4 w-4" />
                        Refuser
                      </button>
                    </div>
                  )}
                  {friendshipStatus === "accepted" && (
                    <div className="space-y-2">
                      <span className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700">
                        <UserCheck className="h-4 w-4" />
                        Ami
                      </span>
                      <button
                        type="button"
                        disabled={isFriendLoading}
                        onClick={() => handleFriendRespond("remove")}
                        className="w-full rounded-2xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-100 disabled:opacity-60"
                      >
                        Retirer des amis
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Signaler */}
            {!isOwnProfile && profile?.id && (
              <div className="rounded-[28px] border border-white/70 bg-white/85 p-5 shadow-sm">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Signaler ce profil
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/signaler/joueur/${profile.id}`)}
                  className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-bold text-rose-700 hover:bg-rose-100"
                >
                  <Flag className="h-4 w-4" />
                  Signaler ce joueur
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
